import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import ProductItem from '../../../components/product-item'
import WishlistToggle from '../../../components/wishlist-toggle'
import {Flex, IconButton} from '@chakra-ui/react'
import {BinIcon} from '../../../components/icons'
import QuantityPicker from '../../../components/quantity-picker'
import {useIntl} from 'react-intl'
import CartItemMessage from './cart-item-message'
import {useDerivedProduct} from '../../../hooks'

const MAP_ERROR_CODE_TO_MESSAGE = {
    INCOMPATIBLE_ITEMS: ({error, item, product}) => ({
        variant: 'incompatible',
        values: {
            masterProductName: error.incompatibleItems[0],
            incompatibleProductName: product?.master?.name || product?.name || item.productName
        }
    }),
    NOT_AVAILABLE: {variant: 'unavailable'},
    EXCEED_AVAILABILITY: ({derivedProduct}) => ({
        variant: 'overquantity',
        values: {quantity: derivedProduct.maxOrderQuantity}
    })
}

const CartLineItem = ({
    item,
    product,
    quantity,
    isBonusItem,
    isLoading,
    onChangeQuantity,
    onRemove
}) => {
    const {formatMessage} = useIntl()
    const derivedProduct = useDerivedProduct(product)
    const [stockMessage, setStockMessage] = useState(null)

    // list of message parameters from the line item's c_errors list
    const messages = useMemo(() => {
        const messages =
            item.c_errors
                ?.map((stringifiedError) => {
                    try {
                        const error = JSON.parse(stringifiedError)
                        const messageParameters = MAP_ERROR_CODE_TO_MESSAGE[error?.code]
                        if (messageParameters instanceof Function) {
                            return messageParameters({error, item, product, derivedProduct})
                        }
                        return messageParameters
                    } catch {
                        /* ignore errors */
                    }
                })
                .filter(Boolean) || []
        if (
            stockMessage &&
            !messages.find((message) =>
                ['overstock', 'overquantity', 'unavailable'].includes(message.variant)
            )
        ) {
            messages.push(stockMessage)
        }
        return messages
    }, [derivedProduct, item, stockMessage, product])

    const hasProduct = !!product
    // handle stock messages when quantity changes
    useEffect(() => {
        if (hasProduct) {
            if (derivedProduct.maxOrderQuantity === 0) {
                setStockMessage({variant: 'unavailable'})
                return
            }
            if (item.quantity > derivedProduct.maxCartQuantity) {
                setStockMessage({
                    variant: 'overquantity',
                    values: {quantity: derivedProduct.maxOrderQuantity}
                })
                return
            }
            if (item.quantity > derivedProduct.maxOrderQuantity) {
                setStockMessage({
                    variant: 'overstock',
                    values: {quantity: derivedProduct.maxOrderQuantity}
                })
                return
            }
        }
        setStockMessage(null)
    }, [hasProduct, item.quantity, derivedProduct.maxCartQuantity, derivedProduct.maxOrderQuantity])

    const onChange = useCallback(
        (_, value) => {
            if (value > derivedProduct.maxOrderQuantity) {
                if (derivedProduct.maxOrderQuantity > 0) {
                    setStockMessage({
                        variant: 'overquantity',
                        values: {quantity: derivedProduct.maxOrderQuantity}
                    })
                }
                return false
            }
            setStockMessage(null)

            return onChangeQuantity ? onChangeQuantity(item, value) : true
        },
        [onChangeQuantity, item, derivedProduct.maxOrderQuantity]
    )

    if (!item) {
        return null
    }

    return (
        <Flex
            flexDirection="column"
            data-testid={`cart-line-item-${item.itemId}`}
            role="group"
            aria-label={item.productName}
        >
            {messages.map((message, index) => (
                <CartItemMessage {...message} key={index} />
            ))}

            <ProductItem
                loading="lazy"
                variant="cart"
                product={product}
                productLineItem={item}
                quantity={quantity}
                primaryAction={
                    product ? (
                        <WishlistToggle product={product} variant="cart" />
                    ) : (
                        <IconButton
                            icon={<BinIcon boxSize={5} />}
                            variant="cartSecondary"
                            onClick={onRemove && (() => onRemove(item))}
                            aria-label={formatMessage({
                                id: 'global.remove',
                                defaultMessage: 'Remove'
                            })}
                        />
                    )
                }
                secondaryActions={
                    product ? (
                        isBonusItem || derivedProduct.maxOrderQuantity === 0 ? (
                            <IconButton
                                icon={<BinIcon boxSize={5} />}
                                variant="cartSecondary"
                                onClick={onRemove && (() => onRemove(item))}
                                aria-label={formatMessage({
                                    id: 'global.remove',
                                    defaultMessage: 'Remove'
                                })}
                            />
                        ) : (
                            <QuantityPicker
                                removable
                                containerProps={{
                                    maxWidth: '10.625rem'
                                }}
                                minQuantity={derivedProduct.minOrderQuantity}
                                maxQuantity={derivedProduct.maxOrderQuantity}
                                quantity={quantity || item.quantity}
                                quantityStep={derivedProduct.stepQuantity}
                                onChange={onChange}
                                onRemove={onRemove && (() => onRemove(item))}
                            />
                        )
                    ) : null
                }
                showLoading={isLoading}
            />
        </Flex>
    )
}

CartLineItem.propTypes = {
    item: PropTypes.object,
    product: PropTypes.object,
    quantity: PropTypes.number,
    isBonusItem: PropTypes.bool,
    isLoading: PropTypes.bool,
    onChangeQuantity: PropTypes.func,
    onRemove: PropTypes.func
}

export default CartLineItem
